import { useMemo } from "react";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";

import { cn } from "../ui/cn";

/**
 * A phone number as two controls: which country, and the number itself.
 *
 * One box was ambiguous in the way that matters. "0161 496 0000" and
 * "+44 161 496 0000" are the same number, "212 555 0123" is a different number
 * depending on where you are standing, and none of that is visible while
 * somebody types. Naming the country makes what is stored -- E.164, always --
 * something the person entering it can see they got right.
 *
 * Validation is the same `libphonenumber-js` the API uses, so the two agree by
 * construction rather than by two people keeping two rules in step. It runs
 * here only to answer sooner: the server parses the number again on the way in
 * and its answer is the one that decides. See `apps/api/src/crm/phone.ts`.
 */

/** Where this desk is, and so what the picker opens on. */
export const DEFAULT_COUNTRY: CountryCode = "GB";

/**
 * The countries this desk rings often enough to want at the top.
 *
 * A pinned handful rather than a "recently used" list: two hundred-odd
 * countries in one alphabetical run means scrolling past Afghanistan to reach
 * the United Kingdom on nearly every contact anybody here has ever added.
 */
const PINNED: CountryCode[] = ["GB", "IE", "US"];

/**
 * The flag, from the country code itself.
 *
 * Two regional indicator letters, which every platform draws as a flag -- no
 * image set to ship, nothing to keep in step with the country list, and it
 * cannot disagree with the code beside it because it *is* the code.
 */
function flag(country: CountryCode): string {
  return String.fromCodePoint(
    ...[...country].map((letter) => 0x1f1a5 + letter.charCodeAt(0)),
  );
}

/** Country names in the reader's own language, from the platform. */
const NAMES = new Intl.DisplayNames(undefined, { type: "region" });

function countryName(country: CountryCode): string {
  try {
    return NAMES.of(country) ?? country;
  } catch {
    return country;
  }
}

/**
 * Split a stored E.164 number back into the two controls.
 *
 * Anything unparseable comes back as-is in the number box under the default
 * country, rather than being dropped. Rows predating this field hold whatever
 * somebody once typed, and opening such a contact should show that text so it
 * can be corrected -- silently emptying the box would look like the number had
 * been lost, and saving would then really lose it.
 */
export function splitPhone(stored: string | null | undefined): {
  country: CountryCode;
  number: string;
} {
  if (!stored) return { country: DEFAULT_COUNTRY, number: "" };

  const parsed = parsePhoneNumberFromString(stored);
  if (!parsed?.country) return { country: DEFAULT_COUNTRY, number: stored };

  return {
    country: parsed.country,
    // Grouped the way that country writes it, not as a run of digits.
    number: parsed.formatNational(),
  };
}

/**
 * A stored number as somebody would read it aloud.
 *
 * E.164 is the right thing to store and the wrong thing to show: nobody in
 * Manchester recognises their own office as `+441614960000`. A number from this
 * country is shown the way this country writes it, and a foreign one keeps its
 * country code, because that is the part that tells you it is foreign.
 *
 * `href` should still be the raw stored value -- `tel:` wants E.164, and the
 * grouping that helps a person read it is noise to a dialler.
 */
export function formatPhone(stored: string | null | undefined): string {
  if (!stored) return "";

  const parsed = parsePhoneNumberFromString(stored);
  if (!parsed) return stored;

  return parsed.country === DEFAULT_COUNTRY
    ? parsed.formatNational()
    : parsed.formatInternational();
}

/** Whether what is in the box is a number the API will accept. */
export function isValidPhone(value: string, country: CountryCode): boolean {
  if (!value.trim()) return true; // No number is a legitimate answer.
  return parsePhoneNumberFromString(value, country)?.isValid() ?? false;
}

export function PhoneField({
  country,
  number,
  onCountryChange,
  onNumberChange,
}: {
  country: CountryCode;
  number: string;
  onCountryChange: (country: CountryCode) => void;
  onNumberChange: (number: string) => void;
}) {
  /* Built once: two hundred countries through Intl and a sort is not work to
     repeat on every keystroke in the box beside it. */
  const options = useMemo(() => {
    const rest = getCountries()
      .filter((entry) => !PINNED.includes(entry))
      .map((entry) => ({ country: entry, name: countryName(entry) }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return [
      ...PINNED.map((entry) => ({ country: entry, name: countryName(entry) })),
      ...rest,
    ];
  }, []);

  const valid = isValidPhone(number, country);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-2">
        {/*
          The select carries its own label because it is not the field's only
          control -- `Field` labels the pair, and a screen reader reaching this
          one otherwise hears "Phone" twice and never hears which half it is on.
        */}
        <select
          aria-label="Country code"
          value={country}
          onChange={(event) => onCountryChange(event.target.value as CountryCode)}
          className="h-8 w-28 shrink-0 rounded-md border border-line bg-app px-1 text-sm text-content outline-none focus:border-brand-400"
        >
          {options.map(({ country: entry, name }) => (
            /*
              Collapsed to flag and dialling code, with the full name in the
              title: the closed select is 7rem wide and "United Kingdom +44"
              truncates to something that could be four other countries.
            */
            <option key={entry} value={entry} title={`${name} +${getCountryCallingCode(entry)}`}>
              {flag(entry)} +{getCountryCallingCode(entry)}
            </option>
          ))}
        </select>

        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={number}
          onChange={(event) => onNumberChange(event.target.value)}
          placeholder={country === "GB" ? "0161 496 0000" : ""}
          aria-invalid={!valid}
          className={cn(
            "h-8 min-w-0 flex-1 rounded-md border bg-app px-2 text-sm text-content outline-none",
            valid
              ? "border-line focus:border-brand-400"
              : "border-sla-breached focus:border-sla-breached",
          )}
        />
      </div>

      {/*
        Said while typing rather than only on save. The server refuses the same
        number for the same reason, but a round trip to be told the digit you
        just typed is wrong is a round trip you can be spared.
      */}
      {valid ? null : (
        <span className="text-[11px] text-sla-breached">
          Not a valid {countryName(country)} number.
        </span>
      )}
    </div>
  );
}
